import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DefectComment } from '@testflow/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { defectsApi } from '../../lib/defects-api.js';

// #27 (Findings 17/18): der Verlauf am Defect. Kommentare und Status-Wechsel bewusst in EINER
// chronologischen Liste — der Leser soll die Geschichte des Defects an einem Stück sehen und
// nicht zwei Zeitachsen im Kopf zusammenführen müssen.
//
// Einträge sind unveränderlich: kein Bearbeiten, kein Löschen. Produktentscheidung — die
// Testdokumentation fließt in die Abnahme ein, ein Beitrag von vor drei Wochen soll sich heute
// nicht umschreiben lassen. Tippfehler korrigiert man per Folgekommentar.

function Entry({ entry }: { entry: DefectComment }) {
  const { t } = useTranslation();
  const when = new Date(entry.created_at).toLocaleString('de-DE');
  // Verfasser kann null sein: author_user_id ist set-null, der Verlauf überlebt seinen Autor.
  const who = entry.author_name ?? t('defect.thread.unknownAuthor');

  // #204: Titelvorschlag der KI als Systemzeile — ohne Verfasser (author_user_id ist null), also
  // bewusst nicht der „Unbekannt"-Platzhalter von oben.
  if (entry.kind === 'title_ai') {
    return (
      <li className="flex flex-wrap items-baseline gap-x-2 border-l-2 border-[var(--border)] py-1 pl-3 text-xs text-text-muted">
        <span>✦ {t('defect.thread.titleAi', { old: entry.body ?? '' })}</span>
        <span>· {when}</span>
      </li>
    );
  }

  if (entry.kind === 'status_change') {
    return (
      <li className="flex flex-wrap items-baseline gap-x-2 border-l-2 border-[var(--border)] py-1 pl-3 text-xs text-text-muted">
        <span className="uppercase tracking-wide">
          {entry.from_status ? t(`defect.status.${entry.from_status}`) : '—'} →{' '}
          {entry.to_status ? t(`defect.status.${entry.to_status}`) : '—'}
        </span>
        <span>· {who}</span>
        <span>· {when}</span>
      </li>
    );
  }

  return (
    <li className="rounded-card border border-[var(--border)] bg-bg-surface p-3">
      <div className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-text-muted">
        <span className="font-medium text-text-primary">{who}</span>
        <span>{when}</span>
      </div>
      <p className="mt-1 whitespace-pre-wrap text-sm text-text-body">{entry.body}</p>
    </li>
  );
}

export function DefectThread({ projectId, defectId }: { projectId: string; defectId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');

  const { data: entries } = useQuery({
    queryKey: ['defect-comments', projectId, defectId],
    queryFn: () => defectsApi.comments(projectId, defectId),
    enabled: Boolean(projectId && defectId),
  });

  const add = useMutation({
    mutationFn: (body: string) => defectsApi.addComment(projectId, defectId, body),
    onSuccess: () => {
      setDraft('');
      void queryClient.invalidateQueries({ queryKey: ['defect-comments', projectId, defectId] });
    },
  });

  const submit = () => {
    const body = draft.trim();
    if (body) add.mutate(body);
  };

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-text-primary">{t('defect.thread.title')}</h2>
      {entries && entries.length > 0 ? (
        <ul className="space-y-2">
          {entries.map((e) => (
            <Entry key={e.comment_id} entry={e} />
          ))}
        </ul>
      ) : (
        <p className="text-xs text-text-muted">{t('defect.thread.empty')}</p>
      )}
      <div className="space-y-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          aria-label={t('defect.thread.newComment')}
          placeholder={t('defect.thread.placeholder')}
          className="min-h-[72px] w-full rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
        />
        <button
          type="button"
          onClick={submit}
          disabled={add.isPending || draft.trim().length === 0}
          className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
        >
          {t('defect.thread.send')}
        </button>
      </div>
    </div>
  );
}
